// src/pages/HomePage.tsx — Premium Automotive Redesign

import { useEffect, useState, useCallback, useRef } from 'react';
import { Search, MapPin, Fuel, Settings, Calendar, ChevronLeft, ChevronRight, ArrowRight, Phone, Mail, Share2, Facebook, Twitter, Linkedin, Car, Brain, Gauge, Loader2, Zap as ZapIcon, CheckCircle, Sparkles, Shield, TrendingUp, Clock, ChevronDown } from 'lucide-react';
import { supabase, Listing } from '../lib/supabase';
import { carPricesApi, CarModelWithBrand } from '../lib/carPricesApi';
import { Link, useRouter } from '../components/Router';
import { Header } from '../components/Header';
import { BannerDisplay } from '../components/BannerDisplay';
import { trackPageView } from '../utils/analytics';
import { estimatorApi, EstimationResult, EstimationFormData } from '../lib/estimatorApi';

// ─── Interfaces ───────────────────────────────────────────
interface NewsArticle {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  publish_date: string;
  read_time: string;
  category: string;
  image_url: string;
  featured: boolean;
  slug?: string;
}

interface HeroSection {
  id: string;
  title: string;
  subtitle: string;
  image_url: string;
  link_url: string;
  cta_text: string;
  badge?: string;
  display_order: number;
  is_active: boolean;
}

// ─── Helpers ──────────────────────────────────────────────
const getDimensionsFromBadge = (badge?: string) => {
  if (!badge) return { width: 1920, height: 1080 };
  if (badge.includes('|')) {
    const parts = badge.split('|');
    const dimensions = parts[1];
    if (dimensions && dimensions.includes('x')) {
      const [w, h] = dimensions.split('x');
      return { width: parseInt(w, 10) || 1920, height: parseInt(h, 10) || 1080 };
    }
  } else if (badge.includes('x')) {
    const [w, h] = badge.split('x');
    return { width: parseInt(w, 10) || 1920, height: parseInt(h, 10) || 1080 };
  }
  return { width: 1920, height: 1080 };
};

const getBadgeText = (badge?: string) => {
  if (!badge) return '';
  if (badge.includes('|')) return badge.split('|')[0];
  if (badge.includes('x')) return '';
  return badge;
};

// ═══════════════════════════════════════════════════════════
// ▌ PREMIUM ANIMATION SYSTEM — CSS Keyframes & Utilities
// ═══════════════════════════════════════════════════════════
const AnimationStyles = () => (
  <style>{`
    /* ── Scroll Progress Bar ── */
    .scroll-progress-bar {
      position: fixed; top: 0; left: 0; height: 3px; z-index: 9999;
      background: linear-gradient(90deg, #D5462E, #e8694f, #D5462E);
      background-size: 200% 100%;
      animation: gradientShift 3s ease infinite;
      transition: width 0.1s linear;
      box-shadow: 0 0 12px rgba(213,70,46,0.4);
    }

    /* ── Keyframes ── */
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(36px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeInDown {
      from { opacity: 0; transform: translateY(-24px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeInLeft {
      from { opacity: 0; transform: translateX(-48px); }
      to { opacity: 1; transform: translateX(0); }
    }
    @keyframes fadeInRight {
      from { opacity: 0; transform: translateX(48px); }
      to { opacity: 1; transform: translateX(0); }
    }
    @keyframes fadeInScale {
      from { opacity: 0; transform: scale(0.88); }
      to { opacity: 1; transform: scale(1); }
    }
    @keyframes marqueeScroll {
      0% { transform: translateX(0); }
      100% { transform: translateX(calc(-50% - 0.75rem)); }
    }
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    @keyframes float {
      0%, 100% { transform: translateY(0) rotate(0deg); }
      33% { transform: translateY(-12px) rotate(1.5deg); }
      66% { transform: translateY(-6px) rotate(-1deg); }
    }
    @keyframes floatSlow {
      0%, 100% { transform: translateY(0) scale(1); }
      50% { transform: translateY(-18px) scale(1.02); }
    }
    @keyframes kenBurns {
      0% { transform: scale(1); }
      100% { transform: scale(1.06); }
    }
    @keyframes glowPulse {
      0%, 100% { opacity: 0.4; transform: scale(1); }
      50% { opacity: 0.7; transform: scale(1.05); }
    }
    @keyframes gradientShift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes bounceGentle {
      0%, 100% { transform: translateY(0) translateX(-50%); }
      50% { transform: translateY(8px) translateX(-50%); }
    }
    @keyframes slideIndicators {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes rotateBorder {
      0% { --border-angle: 0deg; }
      100% { --border-angle: 360deg; }
    }
    @keyframes revealLine {
      from { transform: scaleX(0); }
      to { transform: scaleX(1); }
    }
    @keyframes dotPulse {
      0%, 100% { opacity: 0.3; }
      50% { opacity: 1; }
    }

    /* ── Reveal System ── */
    .reveal {
      opacity: 0; transform: translateY(36px);
      transition: opacity 0.9s cubic-bezier(0.16, 1, 0.3, 1), transform 0.9s cubic-bezier(0.16, 1, 0.3, 1);
    }
    .reveal.revealed { opacity: 1; transform: translateY(0); }

    .reveal-left {
      opacity: 0; transform: translateX(-48px);
      transition: opacity 0.9s cubic-bezier(0.16, 1, 0.3, 1), transform 0.9s cubic-bezier(0.16, 1, 0.3, 1);
    }
    .reveal-left.revealed { opacity: 1; transform: translateX(0); }

    .reveal-right {
      opacity: 0; transform: translateX(48px);
      transition: opacity 0.9s cubic-bezier(0.16, 1, 0.3, 1), transform 0.9s cubic-bezier(0.16, 1, 0.3, 1);
    }
    .reveal-right.revealed { opacity: 1; transform: translateX(0); }

    .reveal-scale {
      opacity: 0; transform: scale(0.88);
      transition: opacity 0.9s cubic-bezier(0.16, 1, 0.3, 1), transform 0.9s cubic-bezier(0.16, 1, 0.3, 1);
    }
    .reveal-scale.revealed { opacity: 1; transform: scale(1); }

    .stagger-1 { transition-delay: 0.08s; }
    .stagger-2 { transition-delay: 0.16s; }
    .stagger-3 { transition-delay: 0.24s; }
    .stagger-4 { transition-delay: 0.32s; }
    .stagger-5 { transition-delay: 0.40s; }
    .stagger-6 { transition-delay: 0.48s; }

    /* ── Hover Effects ── */
    .hover-lift {
      transition: transform 0.55s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.55s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.4s ease;
    }
    .hover-lift:hover {
      transform: translateY(-8px);
      box-shadow: 0 24px 48px -12px rgba(0,0,0,0.08), 0 8px 16px -8px rgba(0,0,0,0.04);
    }

    .hover-lift-subtle {
      transition: transform 0.5s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.5s ease;
    }
    .hover-lift-subtle:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 24px -8px rgba(0,0,0,0.06);
    }

    .img-zoom img {
      transition: transform 1.8s cubic-bezier(0.16, 1, 0.3, 1);
    }
    .img-zoom:hover img {
      transform: scale(1.08);
    }

    /* ── Tilt Card (CSS-only) ── */
    .tilt-card { perspective: 1200px; }
    .tilt-card .tilt-inner {
      transition: transform 0.6s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.6s ease;
      transform-style: preserve-3d;
    }
    .tilt-card:hover .tilt-inner {
      transform: rotateY(1.5deg) rotateX(-1.5deg) translateZ(8px);
      box-shadow: 0 28px 56px -16px rgba(0,0,0,0.1), 0 8px 20px -8px rgba(0,0,0,0.05);
    }

    /* ── Marquee ── */
    .marquee-container {
      overflow: hidden;
      -webkit-mask-image: linear-gradient(to right, transparent 0%, black 4%, black 96%, transparent 100%);
      mask-image: linear-gradient(to right, transparent 0%, black 4%, black 96%, transparent 100%);
    }
    .marquee-track {
      display: flex;
      gap: 1.5rem;
      width: max-content;
      animation: marqueeScroll var(--marquee-duration, 50s) linear infinite;
    }
    .marquee-track:hover {
      animation-play-state: paused;
    }

    /* ── Shimmer Loading ── */
    .shimmer-bg {
      background: linear-gradient(90deg, #f5f5f5 25%, #ebebeb 37%, #f5f5f5 63%);
      background-size: 200% 100%;
      animation: shimmer 1.8s ease-in-out infinite;
    }

    /* ── Glassmorphism ── */
    .glass {
      background: rgba(255,255,255,0.72);
      backdrop-filter: blur(24px) saturate(180%);
      -webkit-backdrop-filter: blur(24px) saturate(180%);
      border: 1px solid rgba(255,255,255,0.4);
    }
    .glass-dark {
      background: rgba(15,23,42,0.6);
      backdrop-filter: blur(24px) saturate(180%);
      -webkit-backdrop-filter: blur(24px) saturate(180%);
      border: 1px solid rgba(255,255,255,0.08);
    }

    /* ── Ken Burns ── */
    .ken-burns-active {
      animation: kenBurns 18s ease-in-out forwards;
    }

    /* ── Floating Decorations ── */
    .float-element {
      animation: float 7s ease-in-out infinite;
    }
    .float-element-slow {
      animation: floatSlow 10s ease-in-out infinite;
    }

    /* ── Hero Scroll Indicator ── */
    .scroll-indicator {
      animation: bounceGentle 2.5s ease-in-out infinite;
    }

    /* ── Accent Line Reveal ── */
    .accent-line {
      transform-origin: center;
      animation: revealLine 1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    }

    /* ── Section Transition Gradient ── */
    .section-fade-top {
      position: relative;
    }
    .section-fade-top::before {
      content: '';
      position: absolute; top: 0; left: 0; right: 0; height: 80px;
      background: linear-gradient(to bottom, var(--fade-from, white), transparent);
      pointer-events: none; z-index: 2;
    }
    .section-fade-bottom {
      position: relative;
    }
    .section-fade-bottom::after {
      content: '';
      position: absolute; bottom: 0; left: 0; right: 0; height: 80px;
      background: linear-gradient(to top, var(--fade-to, white), transparent);
      pointer-events: none; z-index: 2;
    }

    /* ── Dot Pulse (for loading) ── */
    .dot-pulse span {
      animation: dotPulse 1.4s ease-in-out infinite;
    }
    .dot-pulse span:nth-child(2) { animation-delay: 0.2s; }
    .dot-pulse span:nth-child(3) { animation-delay: 0.4s; }

    /* ── Card Shine Effect ── */
    .card-shine {
      position: relative; overflow: hidden;
    }
    .card-shine::after {
      content: '';
      position: absolute; top: -50%; left: -50%;
      width: 200%; height: 200%;
      background: linear-gradient(
        to bottom right,
        transparent 40%,
        rgba(255,255,255,0.08) 45%,
        rgba(255,255,255,0.15) 50%,
        rgba(255,255,255,0.08) 55%,
        transparent 60%
      );
      transform: translateX(-100%) translateY(-100%) rotate(45deg);
      transition: transform 2.4s cubic-bezier(0.16, 1, 0.3, 1);
      pointer-events: none; z-index: 5;
    }
    .card-shine:hover::after {
      transform: translateX(60%) translateY(60%) rotate(45deg);
    }

    /* ── Magnetic Button ── */
    .btn-magnetic {
      transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease, background-color 0.3s ease;
    }
    .btn-magnetic:hover {
      box-shadow: 0 8px 24px -4px rgba(213,70,46,0.35);
    }
    .btn-magnetic:active {
      transform: scale(0.97);
    }

    /* ── Hero Overlay Gradient Enhancement ── */
    .hero-gradient-overlay {
      background:
        linear-gradient(to top, rgba(2,6,23,0.85) 0%, rgba(2,6,23,0.3) 40%, rgba(2,6,23,0.1) 70%, rgba(2,6,23,0.2) 100%),
        linear-gradient(to right, rgba(2,6,23,0.4) 0%, transparent 50%);
    }

    /* ── Service Card Icon Container ── */
    .service-icon-wrap {
      transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
    }
    .group:hover .service-icon-wrap {
      transform: scale(1.08) rotate(-3deg);
    }

    /* ── News Card Date Badge ── */
    .date-badge {
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
    }

    /* ── Estimator Form Focus Glow ── */
    .estimator-select:focus,
    .estimator-input:focus {
      border-color: rgba(213,70,46,0.4);
      box-shadow: 0 0 0 4px rgba(213,70,46,0.06), 0 1px 2px rgba(0,0,0,0.04);
    }

    /* ── No-scrollbar ── */
    .no-scrollbar::-webkit-scrollbar { display: none; }
    .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

    /* ── Reduced Motion ── */
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.15ms !important;
      }
      .reveal, .reveal-left, .reveal-right, .reveal-scale {
        opacity: 1; transform: none;
      }
      .marquee-track { animation: none !important; }
      .scroll-progress-bar { display: none; }
      .ken-burns-active { animation: none !important; }
      .float-element, .float-element-slow { animation: none !important; }
      .scroll-indicator { animation: none !important; }
    }
        /* ═══════════════════════════════════════════════════
       CRITICAL FALLBACK — Custom Utility Classes
       These ensure styles survive Vercel production builds
       even if global CSS purging or caching acts up.
    ═══════════════════════════════════════════════════ */

    /* ── Custom Surface Colors (Tailwind fallback) ── */
    .bg-surface-50 { background-color: #F8FAFC !important; }
    .bg-surface-950 { background-color: #020617 !important; }
    .bg-surface-100 { background-color: #F1F5F9 !important; }

    /* ── Section Title ── */
    .section-title {
      font-size: 1.875rem !important;
      font-weight: 700 !important;
      line-height: 1.15 !important;
      letter-spacing: -0.025em !important;
      color: #111827 !important;
    }
    @media (min-width: 768px) {
      .section-title {
        font-size: 2.25rem !important;
      }
    }

    /* ── Section Subtitle ── */
    .section-subtitle {
      font-size: 0.9375rem !important;
      line-height: 1.7 !important;
      color: #6B7280 !important;
      max-width: 36rem !important;
      margin-left: auto !important;
      margin-right: auto !important;
    }

    /* ── Accent Divider ── */
    .divider-accent {
      width: 3rem !important;
      height: 3px !important;
      background-color: #D5462E !important;
      border-radius: 9999px !important;
    }

    /* ── Primary Button ── */
    .btn-primary {
      display: inline-flex !important;
      align-items: center !important;
      gap: 0.5rem !important;
      background-color: #D5462E !important;
      color: #FFFFFF !important;
      padding: 0.75rem 1.5rem !important;
      border-radius: 9999px !important;
      font-size: 0.875rem !important;
      font-weight: 600 !important;
      transition: all 0.3s ease !important;
      text-decoration: none !important;
      border: none !important;
      cursor: pointer !important;
    }
    .btn-primary:hover {
      background-color: #c13e28 !important;
      box-shadow: 0 4px 16px -2px rgba(213, 70, 46, 0.35) !important;
    }
    .btn-primary:active {
      transform: scale(0.97) !important;
    }

    /* ── No-scrollbar ── */
    .no-scrollbar::-webkit-scrollbar { display: none; }
    .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

    /* ── Reduced Motion ── */
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.15ms !important;
      }
      .reveal, .reveal-left, .reveal-right, .reveal-scale {
        opacity: 1; transform: none;
      }
      .marquee-track { animation: none !important; }
      .scroll-progress-bar { display: none; }
      .ken-burns-active { animation: none !important; }
      .float-element, .float-element-slow { animation: none !important; }
      .scroll-indicator { animation: none !important; }
    }
  `}</style>
);

// ─── Custom Hooks ──────────────────────────────────────────

// Enhanced scroll reveal with staggered children support
function useScrollReveal() {
  useEffect(() => {
    const elements = document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.06, rootMargin: '0px 0px -30px 0px' }
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  });
}

// Count-up animation triggered on scroll into view
function useCountUp(end: number, duration: number = 2000, suffix: string = '') {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const hasStarted = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted.current) {
          hasStarted.current = true;
          const startTime = performance.now();

          const animate = (currentTime: number) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 4); // ease-out quart
            setCount(Math.round(eased * end));

            if (progress < 1) {
              requestAnimationFrame(animate);
            }
          };

          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [end, duration]);

  return { count, ref, displayValue: `${count}${suffix}` };
}

// Scroll progress for the top bar
function useScrollProgress() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollTop = window.scrollY;
          const docHeight = document.documentElement.scrollHeight - window.innerHeight;
          setProgress(docHeight > 0 ? Math.min((scrollTop / docHeight) * 100, 100) : 0);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return progress;
}

// ─── Car Database ─────────────────────────────────────────
const vehicleTypes = ['Citadine', 'Berline', 'SUV / 4x4', 'Utilitaire', 'Coupe / Cabriolet', 'Monospace', 'Autre'];
const fuelTypes = ['Essence', 'Diesel', 'Hybride', 'Electrique', 'GPL'];
const transmissions = ['Manuelle', 'Automatique'];
const years = Array.from({ length: 2024 - 2010 + 1 }, (_, i) => 2024 - i);

const loadingMessages = [
  'Analyse du marche tunisien...',
  'Collecte des donnees...',
  'Evaluation des prix en cours...',
  'Analyse des tendances actuelles...',
  'Calcul du prix estime...',
  "Ajustement selon l'annee et le modele...",
  'Finalisation de l\'estimation...',
];

const carDatabase: Record<string, Array<{ name: string; type: string }>> = {
  'ALFA ROMEO': [
    { name: 'Mito', type: 'Citadine' }, { name: 'Giulia', type: 'Berline' },
    { name: 'Giulietta', type: 'Berline' }, { name: 'Stelvio', type: 'SUV / 4x4' },
    { name: 'Tonale', type: 'SUV / 4x4' },
  ],
  AUDI: [
    { name: 'A1', type: 'Citadine' }, { name: 'A3', type: 'Berline' },
    { name: 'A4', type: 'Berline' }, { name: 'A5', type: 'Coupe / Cabriolet' },
    { name: 'A6', type: 'Berline' }, { name: 'A7', type: 'Berline' },
    { name: 'A8', type: 'Berline' }, { name: 'Q2', type: 'SUV / 4x4' },
    { name: 'Q3', type: 'SUV / 4x4' }, { name: 'Q5', type: 'SUV / 4x4' },
    { name: 'Q7', type: 'SUV / 4x4' }, { name: 'Q8', type: 'SUV / 4x4' },
  ],
  BAIC: [{ name: 'Kenbo S2', type: 'SUV / 4x4' }, { name: 'Kenbo S3', type: 'SUV / 4x4' }],
  BESTUNE: [{ name: 'T77', type: 'SUV / 4x4' }],
  BMW: [
    { name: 'Serie 1', type: 'Berline' }, { name: 'Serie 2', type: 'Coupe / Cabriolet' },
    { name: 'Serie 3', type: 'Berline' }, { name: 'Serie 4', type: 'Coupe / Cabriolet' },
    { name: 'Serie 5', type: 'Berline' }, { name: 'Serie 7', type: 'Berline' },
    { name: 'X1', type: 'SUV / 4x4' }, { name: 'X2', type: 'SUV / 4x4' },
    { name: 'X3', type: 'SUV / 4x4' }, { name: 'X4', type: 'SUV / 4x4' },
    { name: 'X5', type: 'SUV / 4x4' }, { name: 'X6', type: 'SUV / 4x4' },
  ],
  BYD: [
    { name: 'Atto3', type: 'SUV / 4x4' }, { name: 'Dolphin', type: 'Citadine' },
    { name: 'F3', type: 'Berline' }, { name: 'King', type: 'Berline' },
    { name: 'Song', type: 'SUV / 4x4' }, { name: 'Tang', type: 'SUV / 4x4' },
  ],
  CHANGAN: [
    { name: 'Hunter', type: 'Utilitaire' }, { name: 'Star Truck', type: 'Utilitaire' },
    { name: 'Star Van', type: 'Utilitaire' },
  ],
  CHERY: [
    { name: 'Arrizo 5', type: 'Berline' }, { name: 'Arrizo 8', type: 'Berline' },
    { name: 'Tiggo 1', type: 'SUV / 4x4' }, { name: 'Tiggo 2', type: 'SUV / 4x4' },
    { name: 'Tiggo 3', type: 'SUV / 4x4' }, { name: 'Tiggo 4', type: 'SUV / 4x4' },
    { name: 'Tiggo 7', type: 'SUV / 4x4' }, { name: 'Tiggo 8', type: 'SUV / 4x4' },
    { name: 'QQ', type: 'Citadine' },
  ],
  CHEVROLET: [
    { name: 'Equinox', type: 'SUV / 4x4' }, { name: 'Groove', type: 'SUV / 4x4' },
    { name: 'Cruze', type: 'Berline' }, { name: 'Sonic', type: 'Citadine' },
    { name: 'Aveo', type: 'Berline' }, { name: 'Spark', type: 'Citadine' },
    { name: 'Captiva', type: 'SUV / 4x4' },
  ],
  CITROEN: [
    { name: 'Berlingo Multispace', type: 'Monospace' }, { name: 'Berlingo Van', type: 'Utilitaire' },
    { name: 'C1', type: 'Citadine' }, { name: 'C3', type: 'Citadine' },
    { name: 'C3 Picasso', type: 'Monospace' }, { name: 'C3 Air-cross', type: 'SUV / 4x4' },
    { name: 'C-Elysee', type: 'Berline' }, { name: 'C4', type: 'Berline' },
    { name: 'C4 X', type: 'Berline' }, { name: 'C4 Cactus', type: 'Berline' },
    { name: 'C5', type: 'Berline' }, { name: 'C5 Air-cross', type: 'SUV / 4x4' },
    { name: 'C4 Picasso', type: 'Monospace' }, { name: 'C6', type: 'Berline' },
    { name: 'Jumpy Fourgon', type: 'Utilitaire' }, { name: 'Jumpy Combi', type: 'Monospace' },
    { name: 'Jumper', type: 'Utilitaire' }, { name: 'Nemo', type: 'Utilitaire' },
    { name: 'Nemo Multispace', type: 'Monospace' },
  ],
  CUPRA: [
    { name: 'Leon', type: 'Berline' }, { name: 'Formentor', type: 'SUV / 4x4' },
    { name: 'Terramar', type: 'SUV / 4x4' },
  ],
  DACIA: [
    { name: 'Logan', type: 'Berline' }, { name: 'Sandero Stepway', type: 'Citadine' },
    { name: 'Dokker', type: 'Utilitaire' }, { name: 'Sandero', type: 'Citadine' },
    { name: 'Duster', type: 'SUV / 4x4' }, { name: 'Logan MCV', type: 'Berline' },
  ],
  DFSK: [
    { name: 'Glory 580', type: 'SUV / 4x4' }, { name: 'K01H', type: 'Utilitaire' },
    { name: 'K02S', type: 'Utilitaire' }, { name: 'K01S', type: 'Utilitaire' },
    { name: 'Glory 500', type: 'SUV / 4x4' },
  ],
  DONGFENG: [
    { name: 'AX4', type: 'SUV / 4x4' }, { name: 'Box', type: 'Citadine' },
    { name: 'Rich 6', type: 'Utilitaire' }, { name: 'Shine', type: 'Berline' },
    { name: 'S50', type: 'Berline' }, { name: 'SX3', type: 'SUV / 4x4' },
    { name: 'T5 EVO', type: 'SUV / 4x4' },
  ],
  DS: [
    { name: 'DS7', type: 'SUV / 4x4' }, { name: 'DS3', type: 'Citadine' },
    { name: 'DS4', type: 'Berline' }, { name: 'DS5', type: 'Berline' },
  ],
  FAW: [{ name: 'Besturn X40', type: 'SUV / 4x4' }],
  FIAT: [
    { name: 'Doblo', type: 'Utilitaire' }, { name: 'Punto', type: 'Citadine' },
    { name: 'Linea', type: 'Berline' }, { name: '500', type: 'Citadine' },
    { name: 'Ducato', type: 'Utilitaire' }, { name: 'Tipo', type: 'Berline' },
    { name: 'Panda', type: 'Citadine' }, { name: 'Qubo', type: 'Utilitaire' },
    { name: 'Fiorino', type: 'Utilitaire' }, { name: 'Fiat 500 X', type: 'SUV / 4x4' },
  ],
  FODAY: [{ name: 'F22 SC', type: 'Utilitaire' }, { name: 'F22 DC', type: 'Utilitaire' }],
  FORD: [
    { name: 'Everest', type: 'SUV / 4x4' }, { name: 'KA', type: 'Citadine' },
    { name: 'Ranger', type: 'Utilitaire' }, { name: 'Fiesta', type: 'Citadine' },
    { name: 'Ecosport', type: 'SUV / 4x4' }, { name: 'Focus', type: 'Berline' },
    { name: 'Transit', type: 'Utilitaire' }, { name: 'Kuga', type: 'SUV / 4x4' },
    { name: 'Fusion', type: 'Berline' }, { name: 'Mustang', type: 'Coupe / Cabriolet' },
  ],
  FOTON: [{ name: 'Tunland G7 SC', type: 'Utilitaire' }, { name: 'Tunland G7 DC', type: 'Utilitaire' }],
  GAC: [
    { name: 'GA4', type: 'Berline' }, { name: 'GS3', type: 'SUV / 4x4' },
    { name: 'GS4', type: 'SUV / 4x4' }, { name: 'EMKOO', type: 'SUV / 4x4' },
    { name: 'EMZOOM', type: 'SUV / 4x4' },
  ],
  GEELY: [
    { name: 'COOLRAY', type: 'SUV / 4x4' }, { name: 'GX3', type: 'SUV / 4x4' },
    { name: 'GC6', type: 'Berline' }, { name: 'Azkarra', type: 'SUV / 4x4' },
  ],
  GMW: [
    { name: 'Poer', type: 'Utilitaire' }, { name: 'M4', type: 'SUV / 4x4' },
    { name: 'C30', type: 'Berline' }, { name: 'H5', type: 'SUV / 4x4' },
    { name: 'Wingle 6', type: 'Utilitaire' }, { name: 'Wingle 5', type: 'Utilitaire' },
    { name: 'Jolion', type: 'SUV / 4x4' }, { name: 'Haval H2', type: 'SUV / 4x4' },
    { name: 'Haval H6', type: 'SUV / 4x4' }, { name: 'Haval H8', type: 'SUV / 4x4' },
    { name: 'Haval H9', type: 'SUV / 4x4' },
  ],
  HONDA: [
    { name: 'Amaze', type: 'Berline' }, { name: 'Civic', type: 'Berline' },
    { name: 'Brio', type: 'Citadine' }, { name: 'Jazz', type: 'Citadine' },
    { name: 'City', type: 'Berline' }, { name: 'HR-V', type: 'SUV / 4x4' },
    { name: 'Accord', type: 'Berline' }, { name: 'CR-V', type: 'SUV / 4x4' },
    { name: 'ZR-V', type: 'SUV / 4x4' },
  ],
  HUANGHAI: [{ name: 'N3', type: 'Utilitaire' }],
  HYUNDAI: [
    { name: 'Tucson', type: 'SUV / 4x4' }, { name: 'Kona', type: 'SUV / 4x4' },
    { name: 'Venue', type: 'SUV / 4x4' }, { name: 'Bayon', type: 'SUV / 4x4' },
    { name: 'Ioniq 5', type: 'SUV / 4x4' }, { name: 'Palisade', type: 'SUV / 4x4' },
    { name: 'Staria', type: 'Monospace' }, { name: 'Grand i10', type: 'Citadine' },
    { name: 'Grand i10 Sedan', type: 'Berline' }, { name: 'i20', type: 'Citadine' },
    { name: 'Accent', type: 'Berline' }, { name: 'i30', type: 'Berline' },
    { name: 'Elantra', type: 'Berline' }, { name: 'Veloster', type: 'Coupe / Cabriolet' },
    { name: 'IX 35', type: 'SUV / 4x4' }, { name: 'H1', type: 'Utilitaire' },
    { name: 'Santa Fe', type: 'SUV / 4x4' }, { name: 'Creta', type: 'SUV / 4x4' },
  ],
  ISUZU: [{ name: 'D-Max SC', type: 'Utilitaire' }, { name: 'D-Max DC', type: 'Utilitaire' }],
  IVECO: [{ name: 'Daily SC', type: 'Utilitaire' }, { name: 'Daily DC', type: 'Utilitaire' }],
  JAC: [{ name: 'JAC T8 Pro DC', type: 'Utilitaire' }],
  JAGUAR: [
    { name: 'E-PACE', type: 'SUV / 4x4' }, { name: 'XF', type: 'Berline' },
    { name: 'XJ', type: 'Berline' }, { name: 'F-Type', type: 'Coupe / Cabriolet' },
    { name: 'XE', type: 'Berline' }, { name: 'F-PACE', type: 'SUV / 4x4' },
  ],
  JEEP: [
    { name: 'Compass', type: 'SUV / 4x4' }, { name: 'Wrangler', type: 'SUV / 4x4' },
    { name: 'Cherokee', type: 'SUV / 4x4' }, { name: 'Renegade', type: 'SUV / 4x4' },
    { name: 'Grand Cherokee', type: 'SUV / 4x4' },
  ],
  JETOUR: [
    { name: 'Dashing', type: 'SUV / 4x4' }, { name: 'X70 Plus', type: 'SUV / 4x4' },
    { name: 'T2', type: 'SUV / 4x4' },
  ],
  JMC: [{ name: 'Vigus SC', type: 'Utilitaire' }, { name: 'Vigus DC', type: 'Utilitaire' }],
  KIA: [
    { name: 'Seltos', type: 'SUV / 4x4' }, { name: 'Xceed', type: 'SUV / 4x4' },
    { name: 'Niro', type: 'SUV / 4x4' }, { name: 'Soul', type: 'SUV / 4x4' },
    { name: 'Stonic', type: 'SUV / 4x4' }, { name: 'EV6', type: 'SUV / 4x4' },
    { name: 'EV9', type: 'SUV / 4x4' }, { name: 'Rio', type: 'Citadine' },
    { name: 'Cerato', type: 'Berline' }, { name: 'Optima', type: 'Berline' },
    { name: 'Carens', type: 'Monospace' }, { name: 'K 2500', type: 'Utilitaire' },
    { name: 'Sorento', type: 'SUV / 4x4' }, { name: 'Quoris', type: 'Berline' },
    { name: 'Sportage', type: 'SUV / 4x4' }, { name: 'Picanto', type: 'Citadine' },
    { name: "Cee'd", type: 'Berline' }, { name: 'K2700', type: 'Utilitaire' },
  ],
  LADA: [{ name: 'Urban', type: 'SUV / 4x4' }],
  LANCIA: [{ name: 'Ypsilon', type: 'Citadine' }],
  'LAND ROVER': [
    { name: 'Range Rover Sport', type: 'SUV / 4x4' }, { name: 'Range Rover Velar', type: 'SUV / 4x4' },
    { name: 'Discovery Sport', type: 'SUV / 4x4' }, { name: 'Defender', type: 'SUV / 4x4' },
    { name: 'Range Rover Evoque', type: 'SUV / 4x4' },
  ],
  MAHINDRA: [
    { name: 'XUV 300', type: 'SUV / 4x4' }, { name: 'XUV 500', type: 'SUV / 4x4' },
    { name: 'KUV 100', type: 'SUV / 4x4' }, { name: 'Pick-Up SC', type: 'Utilitaire' },
    { name: 'Pick-Up DC', type: 'Utilitaire' },
  ],
  MAZDA: [
    { name: 'Mazda 2', type: 'Citadine' }, { name: 'Mazda 3', type: 'Berline' },
    { name: 'Mazda 6', type: 'Berline' }, { name: 'Mazda CX5', type: 'SUV / 4x4' },
    { name: 'Mazda CX7', type: 'SUV / 4x4' }, { name: 'Mazda CX9', type: 'SUV / 4x4' },
    { name: 'Mazda BT-50', type: 'Utilitaire' },
  ],
  'MERCEDES-BENZ': [
    { name: 'Classe A', type: 'Berline' }, { name: 'Classe B', type: 'Monospace' },
    { name: 'Classe C', type: 'Berline' }, { name: 'Classe CL', type: 'Coupe / Cabriolet' },
    { name: 'Classe GL', type: 'SUV / 4x4' }, { name: 'Classe E', type: 'Berline' },
    { name: 'Classe G', type: 'SUV / 4x4' }, { name: 'Classe X', type: 'Utilitaire' },
    { name: 'Sprinter', type: 'Utilitaire' }, { name: 'Vito', type: 'Utilitaire' },
    { name: 'Citan', type: 'Utilitaire' },
  ],
  MG: [
    { name: 'MG 5', type: 'Berline' }, { name: 'MG 6', type: 'Berline' },
    { name: 'MG HS', type: 'SUV / 4x4' }, { name: 'MG EHS', type: 'SUV / 4x4' },
    { name: 'MG RX8', type: 'SUV / 4x4' }, { name: 'MG MARVEL R', type: 'SUV / 4x4' },
    { name: 'MG RX5', type: 'SUV / 4x4' }, { name: 'MG ONE', type: 'SUV / 4x4' },
    { name: 'MG E-ZS', type: 'SUV / 4x4' }, { name: 'MG 3 Hybride', type: 'Citadine' },
    { name: 'MG 4', type: 'Berline' }, { name: 'MG GT', type: 'Berline' },
    { name: 'MG 3', type: 'Citadine' }, { name: 'MG GS', type: 'SUV / 4x4' },
    { name: 'MG ZS', type: 'SUV / 4x4' },
  ],
  MINI: [
    { name: '3 portes', type: 'Citadine' }, { name: '5 portes', type: 'Citadine' },
    { name: 'Countryman', type: 'SUV / 4x4' },
  ],
  MITSUBISHI: [
    { name: 'Eclipse Cross', type: 'SUV / 4x4' }, { name: 'L200', type: 'Utilitaire' },
    { name: 'Lancer', type: 'Berline' }, { name: 'Mirage', type: 'Citadine' },
    { name: 'Attrage', type: 'Berline' }, { name: 'ASX', type: 'SUV / 4x4' },
    { name: 'Pajero', type: 'SUV / 4x4' },
  ],
  NISSAN: [
    { name: 'Micra', type: 'Citadine' }, { name: 'Sunny', type: 'Berline' },
    { name: 'Juke', type: 'SUV / 4x4' }, { name: 'X-trail', type: 'SUV / 4x4' },
    { name: 'Qashqai', type: 'SUV / 4x4' }, { name: 'Navara', type: 'Utilitaire' },
  ],
  OPEL: [
    { name: 'Adam', type: 'Citadine' }, { name: 'Crossland', type: 'SUV / 4x4' },
    { name: 'Mokka', type: 'SUV / 4x4' }, { name: 'Combo Cargo', type: 'Utilitaire' },
    { name: 'Corsa', type: 'Citadine' }, { name: 'Insignia', type: 'Berline' },
    { name: 'Astra', type: 'Berline' }, { name: 'Meriva', type: 'Monospace' },
    { name: 'Zafira', type: 'Monospace' },
  ],
  PEUGEOT: [
    { name: '107', type: 'Citadine' }, { name: '108', type: 'Citadine' },
    { name: 'Landtrek SC', type: 'Utilitaire' }, { name: 'Landtrek dC', type: 'Utilitaire' },
    { name: 'Rifter', type: 'Utilitaire' }, { name: 'Bipper', type: 'Utilitaire' },
    { name: 'Partner', type: 'Utilitaire' }, { name: '206', type: 'Citadine' },
    { name: '206 Plus', type: 'Citadine' }, { name: '207', type: 'Citadine' },
    { name: '208', type: 'Citadine' }, { name: 'Expert', type: 'Utilitaire' },
    { name: 'Expert Combi', type: 'Monospace' }, { name: '301', type: 'Berline' },
    { name: '308', type: 'Berline' }, { name: '407', type: 'Berline' },
    { name: '508', type: 'Berline' }, { name: '1007', type: 'Citadine' },
    { name: '2008', type: 'SUV / 4x4' }, { name: '3008', type: 'SUV / 4x4' },
    { name: '4008', type: 'SUV / 4x4' }, { name: '5008', type: 'SUV / 4x4' },
    { name: 'Boxer', type: 'Utilitaire' }, { name: 'RCZ', type: 'Coupe / Cabriolet' },
    { name: 'Traveller', type: 'Monospace' },
  ],
  PORSCHE: [
    { name: 'Taycan', type: 'Berline' }, { name: 'Boxster', type: 'Coupe / Cabriolet' },
    { name: 'Cayman', type: 'Coupe / Cabriolet' }, { name: 'Cayenne', type: 'SUV / 4x4' },
    { name: '911', type: 'Coupe / Cabriolet' }, { name: 'Panamera', type: 'Berline' },
    { name: 'Macan', type: 'SUV / 4x4' },
  ],
  RENAULT: [
    { name: 'Arkana', type: 'SUV / 4x4' }, { name: 'Express Van', type: 'Utilitaire' },
    { name: 'Espace', type: 'Monospace' }, { name: 'Kwid', type: 'Citadine' },
    { name: 'Master', type: 'Utilitaire' }, { name: 'Austral', type: 'SUV / 4x4' },
    { name: 'Symbol', type: 'Berline' }, { name: 'Clio', type: 'Citadine' },
    { name: 'Captur', type: 'SUV / 4x4' }, { name: 'Talisman', type: 'Berline' },
    { name: 'Fluence', type: 'Berline' }, { name: 'Koleos', type: 'SUV / 4x4' },
    { name: 'Trafic', type: 'Utilitaire' }, { name: 'Twingo', type: 'Citadine' },
    { name: 'Megane', type: 'Berline' }, { name: 'Megane Sedan', type: 'Berline' },
    { name: 'Scenic', type: 'Monospace' }, { name: 'Laguna', type: 'Berline' },
    { name: 'Kadjar', type: 'SUV / 4x4' },
  ],
  SEAT: [
    { name: 'Ateca', type: 'SUV / 4x4' }, { name: 'Tarraco', type: 'SUV / 4x4' },
    { name: 'Ibiza', type: 'Citadine' }, { name: 'Leon', type: 'Berline' },
    { name: 'Altea', type: 'Monospace' }, { name: 'Toledo', type: 'Berline' },
    { name: 'Arona', type: 'SUV / 4x4' }, { name: 'Mii', type: 'Citadine' },
  ],
  SKODA: [
    { name: 'Scala', type: 'Berline' }, { name: 'Kamiq', type: 'SUV / 4x4' },
    { name: 'Karoq', type: 'SUV / 4x4' }, { name: 'Superb', type: 'Berline' },
    { name: 'Kushaq', type: 'SUV / 4x4' }, { name: 'Kodiaq', type: 'SUV / 4x4' },
    { name: 'Fabia', type: 'Citadine' }, { name: 'Rapid', type: 'Berline' },
    { name: 'Octavia', type: 'Berline' }, { name: 'Yeti', type: 'SUV / 4x4' },
  ],
  SOUEAST: [{ name: 'DX3', type: 'SUV / 4x4' }, { name: 'DX7', type: 'SUV / 4x4' }],
  SSANGYONG: [
    { name: 'Musso', type: 'Utilitaire' }, { name: 'Tivoli', type: 'SUV / 4x4' },
    { name: 'Korando', type: 'SUV / 4x4' }, { name: 'Actyon Sports', type: 'Utilitaire' },
    { name: 'Rexton', type: 'SUV / 4x4' },
  ],
  SUZUKI: [
    { name: 'Ertiga', type: 'Monospace' }, { name: 'S-Presso', type: 'Citadine' },
    { name: 'Jimny', type: 'SUV / 4x4' }, { name: 'Dzire', type: 'Berline' },
    { name: 'Vitara', type: 'SUV / 4x4' }, { name: 'Swift', type: 'Citadine' },
    { name: 'Baleno', type: 'Citadine' }, { name: 'Ciaz', type: 'Berline' },
    { name: 'Celerio', type: 'Citadine' },
  ],
  TATA: [
    { name: 'Xenon X2 SC', type: 'Utilitaire' }, { name: 'Xenon X2 DC', type: 'Utilitaire' },
    { name: 'Super Ace SC', type: 'Utilitaire' },
  ],
  TOYOTA: [
    { name: 'Agya', type: 'Citadine' }, { name: 'Aygo', type: 'Citadine' },
    { name: 'Auris', type: 'Berline' }, { name: 'C-HR', type: 'SUV / 4x4' },
    { name: 'Yaris Cross', type: 'SUV / 4x4' }, { name: 'Yaris Sedan', type: 'Berline' },
    { name: 'Prado', type: 'SUV / 4x4' }, { name: 'Prius', type: 'Berline' },
    { name: 'Land Cruiser', type: 'SUV / 4x4' }, { name: 'Hiace', type: 'Utilitaire' },
    { name: 'Yaris', type: 'Citadine' }, { name: 'Avanza', type: 'Monospace' },
    { name: 'Corolla', type: 'Berline' }, { name: 'Corolla Sedan', type: 'Berline' },
    { name: 'Rav 4', type: 'SUV / 4x4' }, { name: 'Hilux', type: 'Utilitaire' },
  ],
  VOLKSWAGEN: [
    { name: 'Beetle', type: 'Coupe / Cabriolet' }, { name: 'Tiguan', type: 'SUV / 4x4' },
    { name: 'T-cross', type: 'SUV / 4x4' }, { name: 'T-roc', type: 'SUV / 4x4' },
    { name: 'Virtus', type: 'Berline' }, { name: 'Touran', type: 'Monospace' },
    { name: 'Transporter', type: 'Utilitaire' }, { name: 'Crafter', type: 'Utilitaire' },
    { name: 'Caddy', type: 'Utilitaire' }, { name: 'Golf 6', type: 'Berline' },
    { name: 'Golf 7', type: 'Berline' }, { name: 'Golf 8', type: 'Berline' },
    { name: 'Sharan', type: 'Monospace' }, { name: 'Jetta', type: 'Berline' },
    { name: 'Amarok', type: 'Utilitaire' }, { name: 'Up', type: 'Citadine' },
    { name: 'Passat', type: 'Berline' }, { name: 'Polo', type: 'Citadine' },
    { name: 'Polo Sedan', type: 'Berline' }, { name: 'Scirocco', type: 'Coupe / Cabriolet' },
  ],
  VOLVO: [
    { name: 'S60', type: 'Berline' }, { name: 'S90', type: 'Berline' },
    { name: 'XC40', type: 'SUV / 4x4' }, { name: 'XC60', type: 'SUV / 4x4' },
    { name: 'XC90', type: 'SUV / 4x4' }, { name: 'EX30', type: 'SUV / 4x4' },
    { name: 'EX90', type: 'SUV / 4x4' },
  ],
  WALLYS: [
    { name: 'Wallys 719', type: 'Citadine' }, { name: 'Wallys 619', type: 'Citadine' },
    { name: 'Wallys IRIS', type: 'Citadine' }, { name: 'Wallys 216', type: 'Utilitaire' },
    { name: 'Wolf', type: 'SUV / 4x4' },
  ],
  ZXAUTO: [{ name: 'Grand Tiger', type: 'Utilitaire' }, { name: 'Terra Lord', type: 'Utilitaire' }],
  Autre: [{ name: 'Autre', type: 'Autre' }],
};
const carBrands = Object.keys(carDatabase);

// ─── Scroll Progress Bar Component ────────────────────────
const ScrollProgressBar = () => {
  const progress = useScrollProgress();
  if (progress < 1) return null;
  return <div className="scroll-progress-bar" style={{ width: `${progress}%` }} />;
};

// ─── Animated Counter Component ───────────────────────────
const AnimatedCounter = ({ end, suffix = '', label, prefix = '' }: { end: number; suffix?: string; label: string; prefix?: string }) => {
  const { count, ref } = useCountUp(end, 2200);
  return (
    <div ref={ref} className="text-center p-5 rounded-2xl bg-white border border-gray-100 hover-lift-subtle card-shine">
      <div className="text-2xl font-bold text-[#D5462E] tabular-nums tracking-tight">
        {prefix}{count.toLocaleString()}{suffix}
      </div>
      <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mt-2">{label}</div>
    </div>
  );
};

// ─── Premium Listing Card with Tilt ───────────────────────
const ListingCard = ({ listing, index }: { listing: Listing; index?: number }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const images = listing.images && listing.images.length > 0 ? listing.images : [];
  const hasMultipleImages = images.length > 1;

  const nextImage = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); setCurrentImageIndex((prev) => (prev + 1) % images.length); };
  const prevImage = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length); };

  return (
    <div className={`tilt-card reveal stagger-${(index || 0) % 4 + 1}`}>
      <div className="tilt-inner bg-white rounded-2xl border border-gray-100 hover:border-gray-200/80 overflow-hidden flex flex-col h-full card-shine">
        <div className="relative aspect-[4/3] bg-gray-50 overflow-hidden img-zoom">
          <Link to={`/listing/${listing.id}`} className="block w-full h-full">
            {images.length > 0 ? (
              <img src={images[currentImageIndex]} alt={`${listing.brand} ${listing.model}`} className="w-full h-full object-cover" loading="lazy" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-200"><Car className="h-14 w-14" /></div>
            )}
          </Link>

          {/* Badges */}
          <div className="absolute top-3 left-3 flex gap-1.5 z-10">
            <span className="glass text-[11px] font-semibold text-gray-800 px-2.5 py-1 rounded-lg shadow-sm">{listing.year}</span>
            {listing.is_new && (
              <span className="bg-emerald-500 text-white px-2.5 py-1 rounded-lg text-[11px] font-semibold shadow-sm shadow-emerald-500/20">Neuf</span>
            )}
          </div>

          {/* Carousel Controls */}
          {hasMultipleImages && (
            <>
              <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 glass text-gray-700 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 z-20 shadow-sm hover:bg-white/90">
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>
              <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 glass text-gray-700 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 z-20 shadow-sm hover:bg-white/90">
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
              <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 z-20">
                {images.slice(0, 5).map((_, idx) => (
                  <button key={idx} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCurrentImageIndex(idx); }}
                    className={`h-1 rounded-full transition-all duration-400 ${idx === currentImageIndex ? 'bg-white w-5 shadow-sm shadow-white/30' : 'bg-white/40 w-1.5 hover:bg-white/60'}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <Link to={`/listing/${listing.id}`} className="flex flex-col flex-grow p-4 group">
          <h3 className="text-sm font-bold text-gray-900 mb-1 line-clamp-1 tracking-tight group-hover:text-[#D5462E] transition-colors duration-300">{listing.brand} {listing.model}</h3>
          <p className="font-bold text-[#D5462E] text-base mb-3">{listing.price.toLocaleString()} <span className="text-xs font-medium text-gray-400">TND</span></p>
          <div className="mt-auto pt-3 border-t border-gray-50 space-y-1.5">
            <div className="flex items-center justify-between text-[11px] text-gray-400">
              <div className="flex items-center gap-1.5"><Gauge className="w-3 h-3" /><span>{listing.mileage.toLocaleString()} km</span></div>
              <div className="flex items-center gap-1.5"><Fuel className="w-3 h-3" /><span>{listing.fuel_type}</span></div>
            </div>
            <div className="flex items-center text-[11px] text-gray-400 gap-1.5"><MapPin className="w-3 h-3" /><span className="truncate">{listing.location}</span></div>
          </div>
        </Link>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// ▌ SHARE BUTTON
// ═══════════════════════════════════════════════════════════
const ShareButton = ({ articleUrl }: { articleUrl: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const getShareUrl = (platform: 'facebook' | 'twitter' | 'linkedin') => {
    const encodedUrl = encodeURIComponent(articleUrl);
    switch (platform) {
      case 'facebook': return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
      case 'twitter': return `https://twitter.com/intent/tweet?url=${encodedUrl}`;
      case 'linkedin': return `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
      default: return '#';
    }
  };

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const close = () => setIsOpen(false);
    setTimeout(() => document.addEventListener('click', close), 10);
    return () => document.removeEventListener('click', close);
  }, [isOpen]);

  return (
    <div className="relative">
      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsOpen(!isOpen); }}
        className="p-2 glass rounded-full text-gray-500 hover:text-gray-700 hover:bg-white/95 transition-all duration-200 shadow-sm">
        <Share2 className="w-3.5 h-3.5" />
      </button>
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 p-1.5 bg-white rounded-xl border border-gray-100 z-20 flex gap-0.5 shadow-xl shadow-black/5"
          style={{ animation: 'fadeInScale 0.25s cubic-bezier(0.16,1,0.3,1) forwards' }}
          onClick={(e) => e.stopPropagation()}>
          <a href={getShareUrl('facebook')} target="_blank" rel="noopener noreferrer" className="p-2.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200"><Facebook className="w-4 h-4" /></a>
          <a href={getShareUrl('twitter')} target="_blank" rel="noopener noreferrer" className="p-2.5 text-gray-400 hover:text-sky-500 hover:bg-sky-50 rounded-lg transition-all duration-200"><Twitter className="w-4 h-4" /></a>
          <a href={getShareUrl('linkedin')} target="_blank" rel="noopener noreferrer" className="p-2.5 text-gray-400 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200"><Linkedin className="w-4 h-4" /></a>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// ▌ NEWS ARTICLES GRID
// ═══════════════════════════════════════════════════════════
const NewsArticlesGrid = () => {
  const [articles, setArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNewsArticles = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase.from('actualite').select('*').eq('featured', true).order('publish_date', { ascending: false }).limit(4);
        if (error) throw error;
        setArticles(data || []);
      } catch (error) { console.error('Error fetching news articles:', error); }
      finally { setLoading(false); }
    };
    fetchNewsArticles();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const monthNames = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aout', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="rounded-2xl border border-gray-100 overflow-hidden">
            <div className="aspect-video shimmer-bg" />
            <div className="p-4 space-y-3">
              <div className="h-4 shimmer-bg rounded-lg w-full" />
              <div className="h-3 shimmer-bg rounded-lg w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (articles.length === 0) {
    return <div className="text-center py-16"><p className="text-gray-400 text-sm">Aucune actualite disponible.</p></div>;
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
      {articles.map((article, idx) => {
        const articleUrl = `${window.location.origin}/actualite/${article.slug || article.id}`;
        return (
          <article key={article.id} className={`reveal stagger-${idx + 1} tilt-card`}>
            <div className="tilt-inner group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-gray-200/80 transition-all duration-500 flex flex-col h-full card-shine">
              <div className="relative aspect-video bg-gray-50 overflow-hidden img-zoom">
                <Link to={`/actualite/${article.slug || article.id}`} className="block h-full w-full">
                  <img src={article.image_url || '/placeholder-image.jpg'} alt={article.title} className="w-full h-full object-cover" loading="lazy"
                    onError={(e) => { e.currentTarget.src = '/placeholder-image.jpg'; }} />
                </Link>
                <div className="absolute top-3 left-3 date-badge bg-black/50 text-white px-2.5 py-1 text-[10px] font-semibold rounded-lg tracking-wide">{formatDate(article.publish_date)}</div>
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-1 group-hover:translate-y-0">
                  <ShareButton articleUrl={articleUrl} />
                </div>
              </div>
              <div className="p-4 flex flex-col flex-grow">
                <Link to={`/actualite/${article.slug || article.id}`} className="block flex-grow">
                  <h3 className="text-sm font-bold text-gray-900 mb-1 hover:text-[#D5462E] transition-colors duration-300 line-clamp-2 leading-snug tracking-tight">{article.title}</h3>
                </Link>
                <Link to={`/actualite/${article.slug || article.id}`} className="text-[11px] text-[#D5462E] font-semibold mt-3 flex items-center gap-1 group/link">
                  Lire <ArrowRight className="w-3 h-3 group-hover/link:translate-x-1 transition-transform duration-300" />
                </Link>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════
// ▌ MAIN HOMEPAGE COMPONENT
// ═══════════════════════════════════════════════════════════
export function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [listings, setListings] = useState<Listing[]>([]);
  const [carModels, setCarModels] = useState<CarModelWithBrand[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [heroSections, setHeroSections] = useState<HeroSection[]>([]);
  const { navigate } = useRouter();

  // Estimator form
  const [estimatorForm, setEstimatorForm] = useState({ brand: '', model: '', year: '', mileage: '', fuelType: '', vehicleType: '', transmission: '', horsepower: '' });
  const [availableModels, setAvailableModels] = useState<Array<{ name: string; type: string }>>([]);
  const [isEstimating, setIsEstimating] = useState(false);
  const [estimationResult, setEstimationResult] = useState<EstimationResult | null>(null);
  const [estimationError, setEstimationError] = useState<string | null>(null);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);

  const heroIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useScrollReveal();

  useEffect(() => {
    trackPageView('/');
    loadFeaturedData();
    fetchHeroSections();
    return () => { if (heroIntervalRef.current) clearInterval(heroIntervalRef.current); };
  }, []);

  // Hero auto-scroll
  useEffect(() => {
    if (heroSections.length === 0) return;
    if (isAutoPlaying) {
      heroIntervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % heroSections.length);
      }, 6500);
    } else {
      if (heroIntervalRef.current) clearInterval(heroIntervalRef.current);
    }
    return () => { if (heroIntervalRef.current) clearInterval(heroIntervalRef.current); };
  }, [isAutoPlaying, heroSections.length]);

  // Loading messages cycle
  useEffect(() => {
    if (!isEstimating) return;
    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      setProgress((prev) => Math.min(prev + 14, 95));
    }, 1500);
    return () => clearInterval(interval);
  }, [isEstimating]);

  const fetchHeroSections = async () => {
    try {
      const { data, error } = await supabase.from('hero_sections').select('*').eq('is_active', true).order('display_order', { ascending: true });
      if (error) throw error;
      setHeroSections(data || []);
    } catch (err) { console.error('Error fetching hero sections:', err); }
  };

  const loadFeaturedData = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const { data: listingsData, error: listingsError } = await supabase.from('listings').select('*').eq('status', 'approved').order('approved_at', { ascending: false }).limit(4);
      if (listingsError) throw listingsError;
      setListings(listingsData || []);
      try {
        const modelsData = await carPricesApi.getModels();
        const featuredModels = modelsData.filter(model => model.is_featured);
        const modelsWithBrands = await Promise.all(
          featuredModels.map(async (model) => {
            if (!model.brand_id) return model;
            if (!model.brand) {
              const { data: brandData } = await supabase.from('car_brands').select('*').eq('id', model.brand_id).single();
              return { ...model, brand: brandData };
            }
            return model;
          })
        );
        setCarModels(modelsWithBrands);
      } catch (modelError) { console.error('Error loading car models:', modelError); setCarModels([]); }
    } catch (error) { console.error('Error loading data:', error); setError('Failed to load data.'); }
    finally { setLoading(false); }
  }, []);

  const handleBrandChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const brand = e.target.value;
    setAvailableModels(carDatabase[brand] || []);
    setEstimatorForm({ ...estimatorForm, brand, model: '', vehicleType: '' });
  };

  const handleModelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const modelName = e.target.value;
    const selectedModel = availableModels.find((m) => m.name === modelName);
    setEstimatorForm({ ...estimatorForm, model: modelName, vehicleType: selectedModel?.type || '' });
  };

  const handleEstimatorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const requiredFields = ['brand', 'model', 'year', 'mileage', 'transmission', 'fuelType'];
    const missingFields = requiredFields.filter(field => !estimatorForm[field as keyof typeof estimatorForm]);
    if (missingFields.length > 0) { alert('Veuillez remplir tous les champs obligatoires.'); return; }
    try {
      setIsEstimating(true); setEstimationError(null); setEstimationResult(null); setCurrentMessageIndex(0); setProgress(0);
      const result = await estimatorApi.getEstimation(estimatorForm as EstimationFormData);
      setProgress(100);
      sessionStorage.setItem('estimationResult', JSON.stringify(result));
      sessionStorage.setItem('estimatorFormData', JSON.stringify(estimatorForm));
      navigate('/estimateur');
    } catch (error) {
      console.error('Estimation error:', error);
      setEstimationError("Impossible d'obtenir l'estimation.");
      const fallbackResult = estimatorApi.getFallbackEstimation(estimatorForm as EstimationFormData);
      sessionStorage.setItem('estimationResult', JSON.stringify(fallbackResult));
      sessionStorage.setItem('estimatorFormData', JSON.stringify(estimatorForm));
      sessionStorage.setItem('useFallback', 'true');
      setProgress(100);
      navigate('/estimateur');
    } finally { setIsEstimating(false); }
  };

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % heroSections.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + heroSections.length) % heroSections.length);
  const goToSlide = (index: number) => setCurrentSlide(index);

  // ─── Select / Input Styling ─────────────────────────────
  const selectClass = "estimator-select w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 outline-none transition-all duration-300 appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400";
  const inputClass = "estimator-input w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 outline-none transition-all duration-300";
  const labelClass = "text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block";

  // Calculate marquee duration based on content
  const marqueeDuration = Math.max(30, carModels.length * 4);

  return (
    <div className="min-h-screen bg-white font-sans">
      <AnimationStyles />
      <ScrollProgressBar />
      <Header />

      {/* ════════════════════════════════════════════════════
          HERO SECTION — Cinematic Fullscreen with Ken Burns
      ════════════════════════════════════════════════════ */}
      <section className="relative w-full bg-surface-950 overflow-hidden">
        {/* ── Mobile Hero ── */}
        <div className="lg:hidden w-full overflow-x-auto snap-x snap-mandatory flex no-scrollbar scroll-smooth">
          {heroSections.map((hero) => (
            <div key={`mob-${hero.id}`} className="snap-center shrink-0 w-full relative">
              <Link to={hero.link_url} className="block w-full">
                <div className="relative h-[55vh] w-full overflow-hidden">
                  <img src={hero.image_url} className="w-full h-full object-cover ken-burns-active" alt={hero.title} loading="eager" />
                  <div className="absolute inset-0 hero-gradient-overlay" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 z-10" style={{ animation: 'fadeInUp 1.8s cubic-bezier(0.16,1,0.3,1) 0.2s both' }}>
                    {hero.badge && getBadgeText(hero.badge) && (
                      <span className="inline-block px-3 py-1 mb-3 text-[10px] font-bold tracking-widest text-white/90 uppercase glass-dark rounded-full">
                        {getBadgeText(hero.badge)}
                      </span>
                    )}
                    <h2 className="text-2xl font-bold text-white leading-tight mb-2 text-balance">{hero.title}</h2>
                    <p className="text-white/55 text-sm leading-relaxed line-clamp-2">{hero.subtitle}</p>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>

        {/* ── Desktop Hero ── */}
        <div className="hidden lg:block relative w-full group" onMouseEnter={() => setIsAutoPlaying(false)} onMouseLeave={() => setIsAutoPlaying(true)}>
          <div className="relative w-full h-[82vh] min-h-[620px] max-h-[920px] overflow-hidden">
            {heroSections.map((hero, index) => (
              <Link key={`img-${hero.id}`} to={hero.link_url}
                className={`absolute inset-0 w-full h-full transition-all duration-[1400ms] ease-out block ${index === currentSlide ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-[1.04] z-0'}`}>
                <img src={hero.image_url} alt={hero.title}
                  className={`w-full h-full object-cover ${index === currentSlide ? 'ken-burns-active' : ''}`}
                  loading={index === 0 ? 'eager' : 'lazy'} />
                <div className="absolute inset-0 hero-gradient-overlay" />
              </Link>
            ))}

            {/* Hero Text Content */}
            <div className="absolute inset-0 z-20 flex items-end pointer-events-none">
              <div className="w-full max-w-7xl mx-auto px-8 pb-24">
                <div className="grid grid-cols-1">
                  {heroSections.map((hero, index) => (
                    <div key={`txt-${hero.id}`}
                      className={`col-start-1 row-start-1 transition-all duration-[800ms] ease-out ${index === currentSlide ? 'opacity-100 translate-y-0 z-10' : 'opacity-0 translate-y-8 z-0 pointer-events-none'}`}>
                      <div className="max-w-2xl pointer-events-auto">
                        {hero.badge && getBadgeText(hero.badge) && (
                          <span className="inline-block px-4 py-1.5 mb-6 text-[11px] font-bold tracking-widest text-white/90 uppercase glass-dark rounded-full"
                            style={{ transitionDelay: index === currentSlide ? '100ms' : '0ms' }}>
                            {getBadgeText(hero.badge)}
                          </span>
                        )}
                        <h1 className="text-4xl xl:text-[3.5rem] font-bold text-white tracking-tight leading-[1.08] mb-4 text-balance">
                          {hero.title}
                        </h1>
                        <p className="text-lg text-white/50 font-light leading-relaxed max-w-lg">{hero.subtitle}</p>
                      </div>
                    </div>
                  ))}
                  <div className="col-start-1 row-start-1 opacity-0 pointer-events-none invisible">
                    <div className="max-w-2xl"><h1 className="text-6xl">P</h1><p className="text-lg">P</p></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Nav Dots */}
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex items-center gap-2.5 z-30">
              {heroSections.map((_, index) => (
                <button key={index} onClick={() => goToSlide(index)}
                  className={`h-1 rounded-full transition-all duration-600 ${index === currentSlide ? 'bg-white w-10 shadow-sm shadow-white/20' : 'bg-white/25 w-2 hover:bg-white/50 hover:w-3'}`}
                />
              ))}
            </div>

            {/* Scroll Indicator */}
            <div className="scroll-indicator absolute bottom-10 right-8 z-30 hidden xl:flex flex-col items-center gap-2 text-white/30">
              <span className="text-[9px] uppercase tracking-widest font-medium" style={{ writingMode: 'vertical-rl' }}>Scroll</span>
              <ChevronDown className="w-4 h-4" />
            </div>

            {/* Prev/Next */}
            <button onClick={prevSlide} className="absolute left-6 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full glass-dark flex items-center justify-center text-white/60 hover:text-white hover:bg-white/15 opacity-0 group-hover:opacity-100 transition-all duration-400 hover:scale-105">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={nextSlide} className="absolute right-6 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full glass-dark flex items-center justify-center text-white/60 hover:text-white hover:bg-white/15 opacity-0 group-hover:opacity-100 transition-all duration-400 hover:scale-105">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════
          BANNER SLOT 1 — Below Hero
      ════════════════════════════════════════════════════ */}
      <BannerDisplay pageLocation="home" positionKey="home-hero" wrapperBgClass="bg-white" />

      {/* ════════════════════════════════════════════════════
          SERVICES SECTION — Premium Value Props
      ════════════════════════════════════════════════════ */}
      <section className="w-full bg-white py-10 lg:py-14 border-b border-gray-100/80 relative overflow-hidden">
        {/* Smaller decorative gradient orb */}
        <div className="absolute -top-24 -right-24 w-[300px] h-[300px] bg-[#D5462E]/[0.02] rounded-full blur-[80px] float-element-slow pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-[250px] h-[250px] bg-blue-500/[0.01] rounded-full blur-[60px] float-element pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-12">
            <span className="inline-block text-[11px] font-bold tracking-[0.2em] text-[#D5462E] uppercase mb-4">NOS SERVICES</span>
            <h2 className="section-title mb-5">Votre partenaire automobile de confiance</h2>
            <div className="divider-accent mx-auto" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            {[
              { icon: <Brain className="w-6 h-6" />, title: 'ESTIMATION IA', desc: "Estimation instantanee et precise grace a notre intelligence artificielle adaptee au marche tunisien.", link: '/estimateur' },
              { icon: <TrendingUp className="w-6 h-6" />, title: 'PRIX DU NEUF', desc: "Consultez les prix actualises des vehicules neufs chez les concessionnaires en Tunisie.", link: '/neuf' },
              { icon: <Search className="w-6 h-6" />, title: 'ANNONCES', desc: "Acces a une vaste selection de vehicules d'occasion verifies dans tout le pays.", link: '/annonces' },
              { icon: <Sparkles className="w-6 h-6" />, title: 'ACTUALITES', desc: "Les dernieres tendances et nouveautes du secteur automobile, mises a jour regulierement.", link: '/actualite' },
            ].map((service, idx) => (
              <Link key={service.title} to={service.link}
                className={`reveal stagger-${idx + 1} group relative p-7 lg:p-8 rounded-2xl border border-gray-100 hover:border-gray-200/80 bg-white transition-all duration-500 hover-lift card-shine`}>
                <div className="service-icon-wrap w-12 h-12 rounded-xl bg-[#D5462E]/[0.06] flex items-center justify-center text-[#D5462E] mb-5 group-hover:bg-[#D5462E] group-hover:text-white transition-all duration-400 group-hover:shadow-lg group-hover:shadow-[#D5462E]/20">
                  {service.icon}
                </div>
                <h3 className="font-bold text-gray-900 text-sm tracking-wide mb-3 group-hover:text-[#D5462E] transition-colors duration-300">{service.title}</h3>
                <p className="text-gray-500 text-[13px] leading-relaxed">{service.desc}</p>
                <div className="mt-6 flex items-center text-[#D5462E] text-xs font-semibold opacity-0 group-hover:opacity-100 transition-all duration-400 translate-y-2 group-hover:translate-y-0">
                  <span>Decouvrir</span>
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5 group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════
          AI ESTIMATOR — Premium Glass Panel
      ════════════════════════════════════════════════════ */}
      <section className="w-full bg-surface-50 py-14 lg:py-20 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-1/2 -left-40 w-[600px] h-[600px] bg-[#D5462E]/[0.03] rounded-full blur-[120px] float-element-slow pointer-events-none -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-emerald-500/[0.02] rounded-full blur-[80px] float-element pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.2em] text-[#D5462E] uppercase mb-4">ESTIMATEUR IA</span>
            <h2 className="section-title mb-5">Estimez la valeur de votre vehicule</h2>
            <div className="divider-accent mx-auto mb-5" />
            <p className="section-subtitle mx-auto">Une evaluation precise et fiable basee sur le marche tunisien.</p>
          </div>

          <div className="reveal lg:grid lg:grid-cols-5 gap-10 items-start">
            {/* Left — Trust Signals */}
            <div className="hidden lg:block lg:col-span-2 space-y-6">
              <div className="space-y-4">
                {[
                  { icon: <Shield className="w-5 h-5" />, title: 'Fiable & Securise', desc: "Estimations basees sur des milliers d'annonces reelles du marche." },
                  { icon: <ZapIcon className="w-5 h-5" />, title: 'Rapide & Gratuit', desc: "Obtenez votre estimation en quelques secondes, sans aucun frais." },
                  { icon: <TrendingUp className="w-5 h-5" />, title: 'Donnees Actuelles', desc: "Mise a jour constante des prix selon les tendances du marche." },
                ].map((item, idx) => (
                  <div key={idx} className={`reveal-left stagger-${idx + 1} group flex gap-4 p-5 rounded-2xl bg-white border border-gray-100 hover:border-gray-200/80 transition-all duration-400 hover-lift-subtle card-shine`}>
                    <div className="w-11 h-11 rounded-xl bg-[#D5462E]/[0.06] flex items-center justify-center text-[#D5462E] flex-shrink-0 group-hover:bg-[#D5462E] group-hover:text-white transition-all duration-400">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm mb-1">{item.title}</h4>
                      <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Animated Counters */}
              <div className="grid grid-cols-3 gap-3 pt-2">
                <AnimatedCounter end={15000} suffix="+" label="Estimations" />
                <AnimatedCounter end={98} suffix="%" label="Precision" />
                <AnimatedCounter end={2} suffix="s" label="Moyenne" />
              </div>
            </div>

            {/* Right — Form */}
            <div className="reveal-right lg:col-span-3">
              <div className="bg-white rounded-3xl border border-gray-100 shadow-[0_4px_32px_rgba(0,0,0,0.04)] p-6 lg:p-8 relative overflow-hidden">
                {/* Subtle corner glow */}
                <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#D5462E]/[0.04] rounded-full blur-[40px] pointer-events-none" />

                <div className="flex items-center gap-3 mb-7 pb-6 border-b border-gray-100 relative z-10">
                  <div className="w-11 h-11 rounded-xl bg-[#D5462E] flex items-center justify-center shadow-lg shadow-[#D5462E]/20">
                    <Brain className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-base">Estimation Rapide</h3>
                    <p className="text-xs text-gray-400">Remplissez les details de votre vehicule</p>
                  </div>
                </div>

                <form onSubmit={handleEstimatorSubmit} className="space-y-5 relative z-10">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Marque *</label>
                      <select required value={estimatorForm.brand} onChange={handleBrandChange} className={selectClass}>
                        <option value="">Selectionnez</option>
                        {carBrands.map((b) => <option key={b} value={b}>{b}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Modele *</label>
                      <select required value={estimatorForm.model} onChange={handleModelChange} disabled={!estimatorForm.brand} className={selectClass}>
                        <option value="">Selectionnez</option>
                        {availableModels.map((m) => <option key={m.name} value={m.name}>{m.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Annee *</label>
                      <select required value={estimatorForm.year} onChange={(e) => setEstimatorForm({ ...estimatorForm, year: e.target.value })} className={selectClass}>
                        <option value="">Selectionnez</option>
                        {years.map((y) => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Kilometrage *</label>
                      <div className="relative">
                        <input type="number" required placeholder="Ex: 85000" value={estimatorForm.mileage}
                          onChange={(e) => setEstimatorForm({ ...estimatorForm, mileage: e.target.value })} className={inputClass} />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-gray-300 uppercase tracking-wider">KM</span>
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Transmission *</label>
                      <select required value={estimatorForm.transmission} onChange={(e) => setEstimatorForm({ ...estimatorForm, transmission: e.target.value })} className={selectClass}>
                        <option value="">Selectionnez</option>
                        {transmissions.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Carburant *</label>
                      <select required value={estimatorForm.fuelType} onChange={(e) => setEstimatorForm({ ...estimatorForm, fuelType: e.target.value })} className={selectClass}>
                        <option value="">Selectionnez</option>
                        {fuelTypes.map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Mobile trust badges */}
                  <div className="lg:hidden flex flex-wrap gap-3 pt-1">
                    {['Fiable', 'Gratuit', 'Instantane'].map((tag) => (
                      <span key={tag} className="inline-flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 px-3 py-1.5 rounded-full">
                        <CheckCircle className="w-3.5 h-3.5 text-[#D5462E]" />{tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex justify-end pt-1">
                    <button type="submit" disabled={isEstimating}
                      className="btn-magnetic flex items-center justify-center gap-2.5 bg-[#D5462E] text-white px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-[#c13e28] disabled:opacity-50">
                      {isEstimating ? (
                        <><Loader2 className="h-4 w-4 animate-spin" /><span>Analyse en cours...</span></>
                      ) : (
                        <><Brain className="h-4 w-4" /><span>Estimer maintenant</span><ArrowRight className="h-4 w-4" /></>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════
          NEWS SECTION
      ════════════════════════════════════════════════════ */}
      <section className="w-full bg-white py-14 lg:py-20 border-b border-gray-100/80 relative overflow-hidden">
        <div className="absolute -top-32 right-1/4 w-[400px] h-[400px] bg-amber-500/[0.02] rounded-full blur-[100px] float-element-slow pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal flex flex-col sm:flex-row sm:items-end sm:justify-between mb-10">
            <div>
              <span className="inline-block text-[11px] font-bold tracking-[0.2em] text-[#D5462E] uppercase mb-4">ACTUALITES</span>
              <h2 className="section-title">Les dernieres nouvelles</h2>
            </div>
            <Link to="/actualite" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-gray-400 hover:text-[#D5462E] transition-colors duration-300 mt-4 sm:mt-0 group">
              Tout voir <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>
          <NewsArticlesGrid />
          <div className="sm:hidden text-center mt-10">
            <Link to="/actualite" className="inline-flex items-center gap-2 text-sm font-semibold text-[#D5462E]">Tout voir <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════
          BANNER SLOT 2 — Below News
      ════════════════════════════════════════════════════ */}
      <BannerDisplay pageLocation="home" positionKey="home-latest-news" wrapperBgClass="bg-white" />

      {/* ════════════════════════════════════════════════════
          NEW VEHICLES — Infinite Marquee Carousel
      ════════════════════════════════════════════════════ */}
      <section className="w-full bg-surface-50 py-14 lg:py-20 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200/60 to-transparent" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10 relative z-10">
          <div className="reveal flex flex-col sm:flex-row sm:items-end sm:justify-between">
            <div>
              <span className="inline-block text-[11px] font-bold tracking-[0.2em] text-[#D5462E] uppercase mb-4">VEHICULES NEUFS</span>
              <h2 className="section-title">Prix chez les concessionnaires</h2>
            </div>
            <Link to="/neuf" className="hidden sm:inline-flex items-center gap-2 btn-primary text-sm mt-4 sm:mt-0 btn-magnetic">
              Voir tous les modeles <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Desktop Infinite Marquee — full width, seamless loop */}
        <div className="hidden lg:block relative">
          <div className="marquee-container">
            {loading ? (
              <div className="marquee-track" style={{ '--marquee-duration': '40s' } as React.CSSProperties}>
                {[...Array(16)].map((_, i) => (
                  <div key={`skel-${i}`} className="bg-white rounded-2xl border border-gray-100 overflow-hidden flex-shrink-0 w-80">
                    <div className="h-48 shimmer-bg" /><div className="p-5 space-y-3"><div className="h-4 shimmer-bg rounded-lg w-3/4" /><div className="h-3 shimmer-bg rounded-lg w-1/2" /></div>
                  </div>
                ))}
              </div>
            ) : carModels.length > 0 ? (
              <div className="marquee-track" style={{ '--marquee-duration': `${marqueeDuration}s` } as React.CSSProperties}>
                {/* Duplicate the items for seamless loop */}
                {[...carModels, ...carModels].map((model, idx) => (
                  <div key={`mq-${idx}`} className="group bg-white rounded-2xl border border-gray-100 hover:border-gray-200/80 transition-all duration-500 overflow-hidden flex-shrink-0 w-80 hover-lift card-shine">
                    <Link to={`/neuf/${model.brand?.slug || model.brand_id}/${model.slug || model.id}`} className="block h-full">
                      <div className="h-48 bg-white relative overflow-hidden flex items-center justify-center">
                        {model.images && model.images.length > 0 ? (
                          <img src={model.images[0]} alt={`${model.brand?.name || ''} ${model.name}`}
                            className="w-full h-full object-contain p-5 transition-transform duration-700 ease-out group-hover:scale-105" loading="lazy" />
                        ) : (
                          <Car className="h-16 w-16 text-gray-200" />
                        )}
                        {/* Subtle gradient at bottom of image area */}
                        <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-white/80 to-transparent pointer-events-none" />
                      </div>
                      <div className="p-5 border-t border-gray-50/80">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-1.5">{model.brand?.name}</p>
                        <h3 className="font-bold text-gray-900 text-[15px] mb-3 tracking-tight line-clamp-1 group-hover:text-[#D5462E] transition-colors duration-300">{model.name}</h3>
                        <div className="flex items-center text-[11px] text-gray-400 mb-4">
                          {model.engine && <span>{model.engine}</span>}
                          {model.engine && model.transmission && <span className="mx-2 text-gray-200">|</span>}
                          {model.transmission && <span>{model.transmission}</span>}
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">A partir de</p>
                            <p className="font-bold text-[#D5462E] text-lg tracking-tight">{model.starting_price ? `${model.starting_price.toLocaleString()} TND` : 'Sur demande'}</p>
                          </div>
                          <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-[#D5462E] transition-all duration-400 group-hover:shadow-lg group-hover:shadow-[#D5462E]/20">
                            <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-white transition-colors duration-300 group-hover:translate-x-0.5" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-gray-400 text-sm">Aucun modele disponible.</p>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Horizontal Scroll */}
        <div className="lg:hidden">
          <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 px-4 pb-6 no-scrollbar scroll-smooth">
            {loading ? (
              [...Array(4)].map((_, i) => (
                <div key={`mskel-${i}`} className="snap-center shrink-0 w-[80vw] max-w-[340px] bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <div className="h-48 shimmer-bg" /><div className="p-5 space-y-3"><div className="h-4 shimmer-bg rounded-lg w-3/4" /><div className="h-3 shimmer-bg rounded-lg w-1/2" /></div>
                </div>
              ))
            ) : (
              carModels.map((model) => (
                <div key={`mob-${model.id}`} className="snap-center shrink-0 w-[80vw] max-w-[340px] group bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <Link to={`/neuf/${model.brand?.slug || model.brand_id}/${model.slug || model.id}`} className="block h-full">
                    <div className="h-52 bg-white relative flex items-center justify-center border-b border-gray-50/80">
                      {model.images && model.images.length > 0 ? (
                        <img src={model.images[0]} className="w-full h-full object-contain p-5 transition-transform duration-500 group-hover:scale-105" alt={model.name} loading="lazy" />
                      ) : <Car className="text-gray-200 w-16 h-16" />}
                    </div>
                    <div className="p-5">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-1">{model.brand?.name}</p>
                      <h3 className="font-bold text-gray-900 text-lg tracking-tight mb-1">{model.name}</h3>
                      <div className="mt-3 pt-3 border-t border-gray-50 flex justify-between items-end">
                        <span className="text-[10px] text-gray-400 uppercase tracking-wider">A partir de</span>
                        <span className="text-[#D5462E] font-bold text-xl tracking-tight">{model.starting_price ? `${model.starting_price.toLocaleString()} TND` : 'N/A'}</span>
                      </div>
                    </div>
                  </Link>
                </div>
              ))
            )}
          </div>
          <div className="text-center mt-8 px-4">
            <Link to="/neuf" className="btn-primary inline-flex items-center gap-2 text-sm w-full sm:w-auto justify-center btn-magnetic">
              Voir tous les modeles <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════
          BANNER SLOT 3 — Between New Vehicles & Listings
      ════════════════════════════════════════════════════ */}
      <BannerDisplay pageLocation="home" positionKey="home-featured-listings" wrapperBgClass="bg-gray-50" />

      {/* ════════════════════════════════════════════════════
          FEATURED LISTINGS — Occasions
      ════════════════════════════════════════════════════ */}
      <section className="w-full bg-white py-14 lg:py-20 border-b border-gray-100/80 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-1/3 -right-48 w-[500px] h-[500px] bg-[#D5462E]/[0.02] rounded-full blur-[100px] float-element pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal flex flex-col sm:flex-row sm:items-end sm:justify-between mb-10">
            <div>
              <span className="inline-block text-[11px] font-bold tracking-[0.2em] text-[#D5462E] uppercase mb-4">OCCASIONS</span>
              <h2 className="section-title">Les meilleures offres</h2>
            </div>
            <Link to="/annonces" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-gray-400 hover:text-[#D5462E] transition-colors duration-300 mt-4 sm:mt-0 group">
              Voir tout <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="rounded-2xl border border-gray-100 overflow-hidden">
                  <div className="aspect-[4/3] shimmer-bg" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 shimmer-bg rounded-lg w-3/4" />
                    <div className="h-5 shimmer-bg rounded-lg w-1/2" />
                    <div className="h-3 shimmer-bg rounded-lg w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <p className="text-sm text-gray-400 mb-5">{error}</p>
              <button onClick={loadFeaturedData} className="btn-primary text-sm btn-magnetic">Reessayer</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {listings.map((listing, idx) => <ListingCard key={listing.id} listing={listing} index={idx} />)}
            </div>
          )}

          {!loading && !error && listings.length === 0 && (
            <div className="text-center py-24">
              <Car className="w-12 h-12 text-gray-200 mx-auto mb-4" />
              <p className="text-gray-400 text-sm">Aucune annonce disponible pour le moment.</p>
            </div>
          )}

          <div className="sm:hidden text-center mt-10">
            <Link to="/annonces" className="inline-flex items-center gap-2 text-sm font-semibold text-[#D5462E]">Voir tout <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════
          BANNER SLOT 4 — Before Support/CTA
      ════════════════════════════════════════════════════ */}
      <BannerDisplay pageLocation="home" positionKey="home-support-banner" wrapperBgClass="bg-gray-50" />

      {/* ════════════════════════════════════════════════════
          CONTACT / CTA SECTION — Premium Dark
      ════════════════════════════════════════════════════ */}
      <section className="w-full bg-surface-950 py-14 lg:py-20 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#D5462E]/[0.06] rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-[300px] h-[300px] bg-blue-500/[0.04] rounded-full blur-[80px] float-element pointer-events-none" />
        <div className="absolute -top-20 -right-20 w-[250px] h-[250px] bg-[#D5462E]/[0.04] rounded-full blur-[60px] float-element-slow pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="reveal text-center mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.2em] text-[#D5462E] uppercase mb-4">SUPPORT</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-5">Besoin d'aide ?</h2>
            <p className="text-gray-400 text-base max-w-lg mx-auto leading-relaxed">Notre equipe est disponible pour vous accompagner a chaque etape.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-xl mx-auto">
            <a href="tel:+21698221229" className="reveal stagger-1 group p-7 rounded-2xl bg-white/[0.04] border border-white/[0.08] hover:border-white/[0.15] hover:bg-white/[0.07] transition-all duration-500 text-center card-shine hover-lift-subtle">
              <div className="w-12 h-12 rounded-xl bg-[#D5462E]/[0.1] flex items-center justify-center mx-auto mb-5 group-hover:bg-[#D5462E]/[0.2] transition-colors duration-400">
                <Phone className="h-5 w-5 text-[#D5462E]" />
              </div>
              <h3 className="font-semibold text-white mb-1.5 text-sm tracking-wide">Appelez-nous</h3>
              <p className="text-gray-500 text-xs mb-4">Lundi-Vendredi, 9h-18h</p>
              <span className="text-[#D5462E] font-semibold text-sm tracking-tight">+216 98 221 229</span>
            </a>

            <a href="mailto:contact@argusautomobile.tn" className="reveal stagger-2 group p-7 rounded-2xl bg-white/[0.04] border border-white/[0.08] hover:border-white/[0.15] hover:bg-white/[0.07] transition-all duration-500 text-center card-shine hover-lift-subtle">
              <div className="w-12 h-12 rounded-xl bg-[#D5462E]/[0.1] flex items-center justify-center mx-auto mb-5 group-hover:bg-[#D5462E]/[0.2] transition-colors duration-400">
                <Mail className="h-5 w-5 text-[#D5462E]" />
              </div>
              <h3 className="font-semibold text-white mb-1.5 text-sm tracking-wide">Ecrivez-nous</h3>
              <p className="text-gray-500 text-xs mb-4">Reponse sous 2h</p>
              <span className="text-[#D5462E] font-semibold text-sm tracking-tight">contact@argusautomobile.tn</span>
            </a>
          </div>

          {/* Social Links */}
          <div className="reveal stagger-3 flex items-center justify-center gap-4 mt-14">
            {[
              { label: 'Facebook', href: '#', icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> },
              { label: 'Instagram', href: '#', icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg> },
              { label: 'YouTube', href: '#', icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg> },
              { label: 'X', href: '#', icon: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
            ].map((social) => (
              <a key={social.label} href={social.href} aria-label={social.label}
                className="w-10 h-10 rounded-full bg-white/[0.06] border border-white/[0.08] flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/[0.12] hover:border-white/[0.18] transition-all duration-300 hover:scale-110">
                {social.icon}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── Estimation Loading Overlay — Premium ── */}
      {isEstimating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ animation: 'fadeInScale 0.3s cubic-bezier(0.16,1,0.3,1) forwards' }}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-surface-950/60 backdrop-blur-xl" />

          {/* Card */}
          <div className="relative bg-white rounded-3xl p-8 lg:p-10 max-w-sm w-full text-center shadow-2xl shadow-black/10 overflow-hidden">
            {/* Subtle glow behind the card */}
            <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-32 h-32 bg-[#D5462E]/[0.08] rounded-full blur-[40px] pointer-events-none" />

            <div className="relative z-10">
              <div className="w-16 h-16 rounded-2xl bg-[#D5462E]/[0.08] flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-7 h-7 text-[#D5462E] animate-spin" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2 tracking-tight">Estimation en cours</h3>
              <p className="text-sm text-gray-500 mb-6 transition-all duration-300 min-h-[1.5em]">{loadingMessages[currentMessageIndex]}</p>

              {/* Progress bar */}
              <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div className="bg-gradient-to-r from-[#D5462E] to-[#e8694f] h-full rounded-full transition-all duration-700 ease-out" style={{ width: `${progress}%` }} />
              </div>

              {/* Animated dots indicator */}
              <div className="flex justify-center gap-1.5 mt-5 dot-pulse">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D5462E]/40" />
                <span className="w-1.5 h-1.5 rounded-full bg-[#D5462E]/40" />
                <span className="w-1.5 h-1.5 rounded-full bg-[#D5462E]/40" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
